import {
  MutationFunction,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import * as API from '../../api/usersAPI'
import { UUID } from 'crypto'
import { UserConflictError } from '../../api/usersAPI'
import { getCurrentUser } from '../auth/UserProvider'
import { getEffectiveOrgUuid } from '../utils'
import { useAuth0 } from '@/utils/auth/auth0'
import { clearDatabaseSearchStorage } from '@/utils/databaseSearchSession'

interface RegistrationResponse {
  message: string
  status: number
  user_uuid?: UUID
  terms_accepted: boolean
  days_left_to_accept?: number
}

export function useCurrentUser(token: string | null, logout?: () => void) {
  const { getAccessTokenSilently } = useAuth0()

  const registrationQuery = useQuery<RegistrationResponse | null, Error>({
    queryKey: ['currentUserRegistered'],
    queryFn: async () => {
      const freshToken = await getAccessTokenSilently()
      return API.checkRegistration(freshToken)
    },
    enabled: !!token,
    staleTime: 0, // Mark as stale immediately so it refetches on page refresh
  })

  // Handle the error in a useEffect
  useEffect(() => {
    if (registrationQuery.error) {
      const error = registrationQuery.error as any
      // Check if it's an "Email already registered" error
      if (!error.message.includes('already registered') && logout) {
        logout()
      }
    }
  }, [registrationQuery.error, logout])

  const query = useQuery({
    queryKey: ['currentUser'],
    queryFn: async () => {
      if (registrationQuery.data && registrationQuery.data.user_uuid) {
        try {
          const freshToken = await getAccessTokenSilently()
          const userData = await API.getUserByUuid({
            user_uuid: registrationQuery.data.user_uuid,
            token: freshToken,
          })

          return userData
        } catch (error) {
          return null
        }
      }
      return Promise.resolve(null)
    },
    enabled: !!token && registrationQuery.isSuccess,
    staleTime: 0, // Mark as stale immediately so it refetches on page refresh
    retry: (failureCount, error) => {
      if (error instanceof UserConflictError) {
        return false
      }
      return failureCount < 3
    },
  })

  const isLoading = query.isLoading || registrationQuery.isLoading

  return {
    userData: query,
    data: registrationQuery,
    updateUser: useUpdateUserMutation(),
    updateUserTermsAccepted: useUserTermsAcceptedMutation(),
    isLoading,
    isError: query.isError || registrationQuery.isError,
    is409Error:
      registrationQuery.error?.message ===
      'User already registered with this email',
  }
}

export function useUserMutation<TData = unknown, TVariables = unknown>(
  apiClientFn: MutationFunction<TData, TVariables>
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: apiClientFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['currentUser'] })
    },
  })
}

export function useUpdateUserMutation() {
  return useUserMutation(API.updateUser)
}

export function useUserTermsAcceptedMutation() {
  return useUserMutation(API.updateUserTermsAccepted)
}

export function useUserByUuidMutation() {
  return useUserMutation(API.getUserByUuid)
}

export default function usePermissions() {
  const { user, token, orgs } = getCurrentUser()
  const { getAccessTokenSilently } = useAuth0()
  const effectiveOrgUuid = getEffectiveOrgUuid(user, orgs)

  const { data, error, isLoading } = useQuery({
    queryKey: ['permissions', effectiveOrgUuid],
    queryFn: async () => {
      if (!effectiveOrgUuid) {
        return Promise.resolve(null)
      }

      const freshToken = await getAccessTokenSilently()
      const response = await API.getOrgUserPermissions(
        freshToken,
        effectiveOrgUuid,
        user?.uuid as UUID
      )
      return response
    },
    enabled: !!token && !!effectiveOrgUuid,
    retry: 3,
  })

  return { permissions: data, error, isLoading }
}

export function useHandleAccountLinking() {
  const {
    handleRedirectCallback,
    getIdTokenClaims,
    isAuthenticated,
    user,
    logout,
    loginWithRedirect,
  } = useAuth0()

  const [linkStatus, setLinkStatus] = useState<
    'idle' | 'success' | 'error' | 'loading'
  >('idle')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  async function completeAccountLinking() {
    // Force logout + login to switch to primary identity
    clearDatabaseSearchStorage()
    await logout({ returnTo: window.location.origin })

    // Wait briefly
    await new Promise((res) => setTimeout(res, 100))

    await loginWithRedirect({
      appState: { returnTo: '/' },
      prompt: 'login',
    })
  }

  useEffect(() => {
    const urlHasAuthParams =
      window.location.search.includes('code=') &&
      window.location.search.includes('state=')

    if (!urlHasAuthParams) return

    const handleLinking = async () => {
      setLinkStatus('loading')
      try {
        const result = await handleRedirectCallback()
        const appState = result?.appState

        if (
          appState?.linkAccount &&
          appState.primaryAuth0Id &&
          appState.token
        ) {
          const secondaryToken = await getIdTokenClaims()
          const token = appState.token
          const secondaryAuth0Id = secondaryToken?.sub

          if (!secondaryAuth0Id || !token) {
            throw new Error('Missing identity or token')
          }

          const secondaryEmail = secondaryToken?.email?.toLowerCase()
          const primaryEmail = appState?.primaryEmail?.toLowerCase()

          if (!secondaryEmail || !primaryEmail) {
            throw new Error('Missing email information for linking')
          }

          if (secondaryEmail !== primaryEmail) {
            throw new Error('Emails do not match — cannot link these accounts')
          }

          const response = await API.linkAuth0Identities(
            token,
            appState.primaryAuth0Id,
            secondaryAuth0Id
          )

          if (!response.ok) {
            const data = await response.json()
            throw new Error('Failed to link accounts')
          }

          setLinkStatus('success')

          // Clear URL
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname
          )
        } else {
          setLinkStatus('idle') // not a linking redirect
        }
      } catch (err: any) {
        // If it's an Auth0 "Invalid state" error and we haven't identified this as a linking attempt, it's likely a normal login callback, so ignore it
        if (err?.message?.includes('Invalid state')) {
          setLinkStatus('idle')
          return
        }

        setErrorMessage(err?.message || 'Unknown error')
        setLinkStatus('error')
      }
    }

    handleLinking()
  }, [handleRedirectCallback, getIdTokenClaims, isAuthenticated, user])

  return { linkStatus, errorMessage, completeAccountLinking }
}
